// FR-AD7: the operator console stays off the public app surface — only someone with a
// `platform_admins` row ever sees a way into it. `GET /ops/whoami` is the server's answer (plain
// `Actor`, no step-up needed), cached per user so the shell and the admin Overview share one call.
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { OpsWhoamiOut } from "../api/types";
import { useSession } from "../session";

const cache = new Map<string, Promise<boolean>>();

export function fetchIsPlatformAdmin(userId: string): Promise<boolean> {
  let pending = cache.get(userId);
  if (!pending) {
    pending = api<OpsWhoamiOut>("GET", "/ops/whoami")
      .then((w) => w.is_platform_admin)
      .catch(() => {
        // A failed lookup hides the entry point; drop it so the next mount asks again.
        cache.delete(userId);
        return false;
      });
    cache.set(userId, pending);
  }
  return pending;
}

/** Test seam: forget cached answers. */
export function resetPlatformAdminCache(): void {
  cache.clear();
}

/** True once the server confirms the signed-in user is Grackle staff; false otherwise. */
export function useIsPlatformAdmin(): boolean {
  const { user } = useSession();
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    setAdmin(false);
    if (!user) return;
    let live = true;
    void fetchIsPlatformAdmin(user.id).then((v) => live && setAdmin(v));
    return () => {
      live = false;
    };
  }, [user]);
  return admin;
}
